import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareText } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";
import { dictionaries } from "@/lib/i18n-dict";

export default function SmsProgramPage() {
  const { locale } = useI18n();
  const L = (en, es) => (locale === "es" ? es : en);
  const [info, setInfo] = useState({ phone: "508-397-0721", email: "bookings@everclean.com" });

  useEffect(() => {
    api.get("/public/settings").then((r) => {
      const d = unwrap(r) || {};
      setInfo({
        phone: d.company_phone || d.support_phone || "508-397-0721",
        email: d.support_email || "bookings@everclean.com",
      });
    }).catch(() => {});
  }, []);

  const consentText = `${dictionaries.en.wizard.step6.sms_consent} ${dictionaries.en.wizard.step6.privacy_policy_link}.`;
  const consentTextEs = `${dictionaries.es.wizard.step6.sms_consent} ${dictionaries.es.wizard.step6.privacy_policy_link}.`;

  const EXAMPLES = [
    {
      title: L("Booking confirmation", "Confirmación de reserva"),
      sample: L(
        "Ever Clean Home: your cleaning is confirmed for Aug 20 at 7–11 AM. Track your booking at evercleanhome.online/track?b=EC-2026-00123. Reply STOP to unsubscribe.",
        "Ever Clean Home: tu limpieza está confirmada para el 20 ago a las 7–11 AM. Sigue tu reserva en evercleanhome.online/track?b=EC-2026-00123. Responde STOP para darte de baja."
      ),
    },
    {
      title: L("Day-before reminder", "Recordatorio del día anterior"),
      sample: L(
        "Ever Clean Home: reminder — your cleaning is tomorrow, Aug 21, at 7–11 AM. Reply STOP to unsubscribe.",
        "Ever Clean Home: recordatorio — tu limpieza es mañana 21 ago a las 7–11 AM. Responde STOP para darte de baja."
      ),
    },
    {
      title: L("Cleaner on the way", "Limpiador en camino"),
      sample: L(
        "Ever Clean Home: your cleaner is on the way and should arrive shortly. Reply STOP to unsubscribe.",
        "Ever Clean Home: tu limpiador está en camino y llega en breve. Responde STOP para darte de baja."
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="sms-program-page">
      <SEO
        title="SMS Program — Ever Clean Home"
        description="How Ever Clean Home's text message notifications work: who sends them, how to opt in, message examples, frequency and how to opt out."
      />
      <Header />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 prose prose-stone">
        <div className="w-14 h-14 rounded-2xl bg-[#1B2A8A]/10 text-[#1B2A8A] grid place-items-center not-prose mb-4">
          <MessageSquareText className="w-7 h-7" />
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter">{L("SMS Program — Ever Clean Home", "Programa de SMS — Ever Clean Home")}</h1>
        <p className="text-sm text-[#4B5563]">{L("Last updated:", "Última actualización:")} {new Date().toLocaleDateString()}</p>

        <h3>{L("Who sends these messages", "Quién envía estos mensajes")}</h3>
        <p>
          {L(
            "Ever Clean Home sends the text messages described on this page. Questions about a message? Email",
            "Ever Clean Home envía los mensajes de texto descritos en esta página. ¿Preguntas sobre un mensaje? Escríbenos a"
          )}{" "}
          <strong>{info.email}</strong> {L("or call", "o llama al")} <strong data-testid="sms-page-phone">{info.phone}</strong>.
        </p>

        <h3>{L("How you sign up", "Cómo te suscribes")}</h3>
        <p>
          {L(
            "You opt in on the last step of our booking form at",
            "Te suscribes en el último paso de nuestro formulario de reserva en"
          )}{" "}
          <Link to="/book">evercleanhome.online/book</Link>. {L(
            "There's a separate, optional checkbox (unchecked by default) below the Terms & Privacy checkbox, with this exact text:",
            "Hay una casilla separada y opcional (desmarcada por defecto) debajo de la casilla de Términos y Privacidad, con este texto exacto:"
          )}
        </p>
        <blockquote data-testid="sms-page-consent-quote">
          &ldquo;{locale === "es" ? consentTextEs : consentText}&rdquo;
        </blockquote>
        <p className="text-sm text-[#4B5563]">
          {L(
            "Checking this box is never required to complete a booking — you can pay and book a cleaning without opting in.",
            "Marcar esta casilla nunca es obligatorio para completar una reserva — puedes pagar y reservar una limpieza sin suscribirte."
          )}
        </p>

        <h3>{L("What you'll receive", "Qué vas a recibir")}</h3>
        <div className="not-prose space-y-3 my-4">
          {EXAMPLES.map((ex) => (
            <div key={ex.title} className="rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid={`sms-example-${ex.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display font-bold text-sm">{ex.title}</div>
              <div className="text-sm text-[#4B5563] mt-1 font-mono">{ex.sample}</div>
            </div>
          ))}
        </div>
        <p>{L("These three are the only kinds of messages we send — no marketing texts.", "Estos tres son los únicos tipos de mensajes que enviamos — sin mensajes de marketing.")}</p>

        <h3>{L("Frequency & cost", "Frecuencia y costo")}</h3>
        <p>{L("Message frequency varies depending on how many bookings you make. Message and data rates may apply.", "La frecuencia de mensajes varía según tus reservas. Pueden aplicar cargos por mensajes y datos.")}</p>

        <h3>{L("Opting out or getting help", "Darte de baja o pedir ayuda")}</h3>
        <p>{L("Reply STOP to any message to unsubscribe at any time. Reply HELP for help.", "Responde STOP a cualquier mensaje para darte de baja en cualquier momento. Responde HELP para ayuda.")}</p>

        <p>
          {L("See our", "Consulta nuestra")} <Link to="/privacy#sms">{L("Privacy Policy", "Política de Privacidad")}</Link> {L("and", "y")}{" "}
          <Link to="/terms">{L("Terms of Service", "Términos de Servicio")}</Link> {L("for more details.", "para más detalles.")}
        </p>
      </div>
      <Footer />
    </div>
  );
}
